import { computed, inject, Injectable, signal } from '@angular/core';
import { INotification } from '@lb/api/mylb/interfaces';
import { NotificationApiService } from '@lb/api/notification/services/notification-api.service';
import { take } from 'rxjs';

/**
 * State for the enrollment overview banners. Pulls the enrollment's notification list from
 * Notification.Api — the server scopes the list to the caller's role on the enrollment
 * (learner / instructor / admin-as-instructor), so no client-side role logic is needed.
 */
@Injectable()
export class EnrollmentNotificationsStateService {
	private readonly notificationApiService = inject(NotificationApiService);

	private readonly _enrollmentID = signal<number | null>(null);
	private readonly _notifications = signal<INotification[]>([]);
	private readonly _loading = signal(false);
	private readonly _loaded = signal(false);
	private readonly _error = signal(false);

	notifications = computed(() => this._notifications());
	topNotification = computed(() => this._notifications()[0] ?? null);
	loading = computed(() => this._loading());
	loaded = computed(() => this._loaded());
	error = computed(() => this._error());

	loadNotifications(enrollmentID: number, bustCache = false): void {
		if (!enrollmentID) return;

		const alreadyLoaded = this._loaded() && this._enrollmentID() === enrollmentID;
		if (alreadyLoaded && !bustCache) return;

		this._enrollmentID.set(enrollmentID);
		this._loading.set(true);
		this._loaded.set(false);
		this._error.set(false);

		this.notificationApiService
			.getEnrollmentNotifications(enrollmentID)
			.pipe(take(1))
			.subscribe({
				next: (notifications) => {
					this._notifications.set(notifications);
					this._loading.set(false);
					this._loaded.set(true);
				},
				error: () => {
					this._notifications.set([]);
					this._loading.set(false);
					this._loaded.set(true);
					this._error.set(true);
				}
			});
	}
}
